import { useEffect, useMemo, useState } from 'react';
import { useForm, type UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  custoAquisicaoFormSchema,
  detalheCustoAquisicaoSchema,
  type CriarCustoAquisicaoDto,
  type CustoAquisicaoFormEntrada,
  type CustoAquisicaoFormSaida,
  type CustoAquisicaoResponse,
  type SimularCustoAquisicaoDto,
} from '@triade/contracts';
import { ehEnfileirado, useCriarCustoAquisicao, useSimularCustoAquisicao } from '@/api/custos-aquisicao';
import { usePreviaImpactoAquisicao } from '@/api/impacto-cascata';
import { useConfirmarImpacto } from '@/components/impacto-cascata/use-confirmar-impacto';
import { ErroFormacao } from '@/components/formacao/avisos-formacao';
import { FornecedorCombobox, type FornecedorResumo } from '@/components/fornecedores';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useDebounced } from '@/hooks/use-debounced';
import { ApiError } from '@/lib/api';
import { decimalParaTextoCampo, ehZero, fracaoParaPercentualTexto, formatarCusto, formatarData, formatarTaxa } from '@/lib/format';
import { cn } from '@/lib/utils';
import { MensagemCampo } from './dados-item';
import { hojeIso } from './tributos-item';

const ROTULO = 'mb-1.5 block text-caption font-medium text-ink-secondary';
const AJUDA = 'mt-1 text-caption text-ink-muted';
const ROTULO_TAXA: Record<'ICMS' | 'IPI' | 'PIS' | 'COFINS', string> = { ICMS: 'ICMS', IPI: 'IPI', PIS: 'PIS', COFINS: 'COFINS' };

/** Valores iniciais: a vigencia mais recente em texto de campo (para editar so o que muda). Sem vigencia: switches padrao da API. */
export function valoresAquisicao(v: CustoAquisicaoResponse | null | undefined, data: string = hojeIso()): CustoAquisicaoFormEntrada {
  return {
    validFrom: data,
    fornecedorId: v?.fornecedorId ?? '',
    basePrice: v ? decimalParaTextoCampo(v.basePrice) : '',
    creditIcms: fracaoParaPercentualTexto(v?.creditIcms),
    switchIcms: v?.switchIcms ?? true,
    creditPis: fracaoParaPercentualTexto(v?.creditPis),
    switchPis: v?.switchPis ?? true,
    creditCofins: fracaoParaPercentualTexto(v?.creditCofins),
    switchCofins: v?.switchCofins ?? true,
    ipiRate: fracaoParaPercentualTexto(v?.ipiRate),
    switchIPI: v?.switchIPI ?? false,
    icmsDifRate: fracaoParaPercentualTexto(v?.icmsDifRate),
    icmsStRate: fracaoParaPercentualTexto(v?.icmsStRate),
    freightRate: fracaoParaPercentualTexto(v?.freightRate),
    insuranceRate: fracaoParaPercentualTexto(v?.insuranceRate),
    creditoIbs: v?.creditoIbs ?? false,
    creditoCbs: v?.creditoCbs ?? false,
    motivo: '',
  };
}

type Formulario = UseFormReturn<CustoAquisicaoFormEntrada, unknown, CustoAquisicaoFormSaida>;

export interface AquisicaoItemProps {
  itemId: string;
  /** Vigencia de hoje (GET /itens/:id/resumo): null = sem custo vigente, undefined = a API nao mostrou (sem custo.ver). */
  vigente: CustoAquisicaoResponse | null | undefined;
  /** Historico de custos do item (GET /custos-aquisicao): daqui saem as vigencias abertas (validTo nulo) por fornecedor. */
  custos?: CustoAquisicaoResponse[];
  /** Informa a tela quando ha uma vigencia digitada e nao registrada (aviso de saida). */
  onDirtyChange?: (dirty: boolean) => void;
  /** Pode registrar (custoAquisicao.editar). Sem a permissao a aba so mostra o formulario somente leitura. */
  podeRegistrar?: boolean;
  /** Pode ver o custo ao vivo (custo.ver: POST /custos-aquisicao/simular). */
  comCusto?: boolean;
}

/**
 * Aba "Aquisicao" (tipos comprados): nova vigencia do custo de aquisicao. Nenhuma conta aqui: o custo ao vivo vem de
 * POST /custos-aquisicao/simular (debounce) e a gravacao de POST /custos-aquisicao. Switches sao independentes das taxas.
 */
export function AquisicaoItem({ itemId, vigente, custos, onDirtyChange, podeRegistrar = true, comCusto = true }: AquisicaoItemProps) {
  const padrao = useMemo(() => valoresAquisicao(vigente), [vigente]);
  const [fornecedor, setFornecedor] = useState<FornecedorResumo | null>(null);
  // Sessao 17: vigencia paralela (outro fornecedor ao mesmo tempo) e marca de preferencial. Fora do formulario de calculo (sem o schema).
  const [paralela, setParalela] = useState(false);
  const [preferencial, setPreferencial] = useState(false);
  const [ok, setOk] = useState<string | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const registrar = useCriarCustoAquisicao();
  const previaImpacto = usePreviaImpactoAquisicao();
  const { confirmar, dialogo } = useConfirmarImpacto();
  const form = useForm<CustoAquisicaoFormEntrada, unknown, CustoAquisicaoFormSaida>({
    resolver: zodResolver(custoAquisicaoFormSchema),
    defaultValues: padrao,
    mode: 'onTouched',
  });
  const { errors, isDirty } = form.formState;

  useEffect(() => {
    if (!form.formState.isDirty) form.reset(padrao);
    setFornecedor(vigente?.fornecedorId && vigente.fornecedorNome ? { id: vigente.fornecedorId, nome: vigente.fornecedorNome } : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [padrao]);
  useEffect(() => onDirtyChange?.(isDirty), [isDirty, onDirtyChange]);

  // Calculo ao vivo: a chave (JSON do corpo valido) e atrasada; so o corpo atrasado vai para a API.
  const valores = form.watch();
  const lido = custoAquisicaoFormSchema.safeParse(valores);
  const chave = lido.success ? JSON.stringify(lido.data.calculo) : '';
  const chaveAtrasada = useDebounced(chave, 400);
  const corpo = useMemo<SimularCustoAquisicaoDto | null>(() => (chaveAtrasada ? (JSON.parse(chaveAtrasada) as SimularCustoAquisicaoDto) : null), [chaveAtrasada]);
  const simulacao = useSimularCustoAquisicao(corpo, { ativo: comCusto });
  const calculando = lido.success && chave !== chaveAtrasada;

  async function enviar(dados: CustoAquisicaoFormSaida) {
    setErro(null);
    setOk(null);
    const dto: CriarCustoAquisicaoDto = { itemId, ...dados.calculo, ...dados.cadastro, paralela, preferencial };
    try {
      // Antes de gravar: impacto em cascata (previa na API, sem gravar). Cancelar nao grava; erro da previa cai no catch abaixo.
      const confirmado = await confirmar(() => previaImpacto.mutateAsync(dto));
      if (!confirmado) return;
      const r = await registrar.mutateAsync(dto);
      if (ehEnfileirado(r)) {
        setOk(
          r.enfileiradas > 0
            ? 'Enviada ao lote de atualização de custos. Ela só vale depois da aprovação em Atualizações de custo; o histórico muda quando o lote for aplicado.'
            : 'Nada a enviar: o custo já vale assim (sem mudança).',
        );
        form.reset(valoresAquisicao(vigente));
        setParalela(false);
        setPreferencial(false);
        return;
      }
      setOk(`Vigência registrada a partir de ${formatarData(r.validFrom)}. A anterior foi encerrada nessa data.`);
      form.reset(valoresAquisicao(r));
      setFornecedor(r.fornecedorId && r.fornecedorNome ? { id: r.fornecedorId, nome: r.fornecedorNome } : null);
      setParalela(false);
      setPreferencial(false);
    } catch (e) {
      setErro(e);
      if (e instanceof ApiError && e.status === 400) {
        for (const i of e.body?.issues ?? []) {
          const campo = i.path.split('.')[0] as keyof CustoAquisicaoFormEntrada;
          if (campo in padrao) form.setError(campo, { message: i.message });
        }
      }
    }
  }

  const campo = (id: string, rotulo: string, nome: keyof CustoAquisicaoFormEntrada, extra?: string) => (
    <div>
      <Label htmlFor={id} className={ROTULO}>
        {rotulo}
      </Label>
      <Input id={id} inputMode="decimal" className="font-mono" placeholder="0" aria-invalid={errors[nome] ? true : undefined} aria-describedby={`${id}-erro`} {...form.register(nome)} />
      {extra && <p className={AJUDA}>{extra}</p>}
      <MensagemCampo id={`${id}-erro`} mensagem={errors[nome]?.message as string | undefined} />
    </div>
  );

  return (
    <div className="grid gap-5 p-5 lg:grid-cols-[minmax(0,1fr)_300px]">
      <div className="lg:col-span-2">
        <VigenciasAbertas custos={custos} />
      </div>
      <form onSubmit={(e) => void form.handleSubmit((d) => enviar(d))(e)} noValidate aria-label="Nova vigência do custo de aquisição" className="flex flex-col gap-4">
        <fieldset disabled={!podeRegistrar} className="contents">
          <div className="grid grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-3.5">
            <div>
              <Label htmlFor="a-data" className={ROTULO}>
                A partir de
              </Label>
              <Input id="a-data" type="date" aria-invalid={errors.validFrom ? true : undefined} aria-describedby={errors.validFrom ? 'a-data-erro' : undefined} {...form.register('validFrom')} />
              <MensagemCampo id="a-data-erro" mensagem={errors.validFrom?.message} />
            </div>
            <div className="sm:col-span-2">
              <span id="a-forn-rotulo" className={ROTULO}>
                Fornecedor
              </span>
              <FornecedorCombobox
                id="a-fornecedor"
                ariaLabel="Fornecedor"
                disabled={!podeRegistrar || !comCusto}
                podeCadastrar={podeRegistrar}
                valor={fornecedor}
                onSelecionar={(f) => {
                  setFornecedor(f);
                  form.setValue('fornecedorId', f?.id ?? '', { shouldDirty: true });
                }}
              />
            </div>
            <div>
              <Label htmlFor="a-base" className={ROTULO}>
                Preço base (R$)
              </Label>
              <Input id="a-base" inputMode="decimal" className="font-mono" placeholder="0,00" aria-invalid={errors.basePrice ? true : undefined} aria-describedby="a-base-erro" {...form.register('basePrice')} />
              <MensagemCampo id="a-base-erro" mensagem={errors.basePrice?.message} />
            </div>
          </div>

          <div className="grid gap-3 rounded-xl border border-dashed border-border p-4 sm:grid-cols-2">
            <label htmlFor="a-paralela" className="flex cursor-pointer items-start gap-2">
              <input id="a-paralela" type="checkbox" className="mt-0.5 size-[18px] accent-primary" aria-describedby="a-paralela-ajuda" checked={paralela} onChange={(e) => setParalela(e.target.checked)} />
              <span>
                Vigência paralela (outro fornecedor ao mesmo tempo)
                <span id="a-paralela-ajuda" className={`block ${AJUDA}`}>
                  Fecha só a vigência aberta do mesmo fornecedor. Sem marcar, fecha todas as vigências abertas do item.
                </span>
              </span>
            </label>
            <label htmlFor="a-preferencial" className="flex cursor-pointer items-start gap-2">
              <input id="a-preferencial" type="checkbox" className="mt-0.5 size-[18px] accent-primary" aria-describedby="a-preferencial-ajuda" checked={preferencial} onChange={(e) => setPreferencial(e.target.checked)} />
              <span>
                Marcar como preferencial
                <span id="a-preferencial-ajuda" className={`block ${AJUDA}`}>
                  Esta é a aquisição usada quando a ficha não fixa um fornecedor. Desmarca as demais vigentes do item.
                </span>
              </span>
            </label>
          </div>

          <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-3.5 rounded-xl border border-border p-4">
            <p className="col-span-full text-small font-semibold">Tributos recuperáveis</p>
            <Recuperavel form={form} switchName="switchIcms" taxaName="creditIcms" rotulo="ICMS recuperável" tributo={ROTULO_TAXA.ICMS} ajuda="Desligado: o ICMS da alíquota entra no custo (não credita)." />
            <Recuperavel form={form} switchName="switchIPI" taxaName="ipiRate" rotulo="IPI recuperável" tributo={ROTULO_TAXA.IPI} ajuda="Ligado: o IPI sai do custo. Desligado: o IPI entra no custo." />
            <Recuperavel form={form} switchName="switchPis" taxaName="creditPis" rotulo="Crédito de PIS" tributo={ROTULO_TAXA.PIS} ajuda="Desligado: o PIS da alíquota entra no custo." />
            <Recuperavel form={form} switchName="switchCofins" taxaName="creditCofins" rotulo="Crédito de COFINS" tributo={ROTULO_TAXA.COFINS} ajuda="Desligado: o COFINS da alíquota entra no custo." />
          </div>

          <div className="grid grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-3.5">
            {campo('a-dif', 'Diferencial de alíquota (%)', 'icmsDifRate')}
            {campo('a-st', 'ICMS-ST (%)', 'icmsStRate')}
            {campo('a-frete', 'Frete (%)', 'freightRate')}
            {campo('a-seguro', 'Seguro (%)', 'insuranceRate')}
          </div>

          <div className="grid gap-3 rounded-xl border border-dashed border-border p-4 sm:grid-cols-2">
            <label htmlFor="a-ibs" className="flex cursor-pointer items-start gap-2">
              <input id="a-ibs" type="checkbox" role="switch" aria-describedby="a-ibs-ajuda" className="mt-0.5 size-[18px] accent-primary" {...form.register('creditoIbs')} />
              <span>
                Crédito de IBS
                <span id="a-ibs-ajuda" className={`block ${AJUDA}`}>
                  Só cadastro: não entra no cálculo do custo.
                </span>
              </span>
            </label>
            <label htmlFor="a-cbs" className="flex cursor-pointer items-start gap-2">
              <input id="a-cbs" type="checkbox" role="switch" aria-describedby="a-cbs-ajuda" className="mt-0.5 size-[18px] accent-primary" {...form.register('creditoCbs')} />
              <span>
                Crédito de CBS
                <span id="a-cbs-ajuda" className={`block ${AJUDA}`}>
                  Só cadastro: não entra no cálculo do custo.
                </span>
              </span>
            </label>
            <p role="note" className="col-span-full rounded-[10px] bg-surface-muted px-3 py-2 text-small text-ink-secondary">
              Aviso: IBS e CBS são só cadastro nesta versão (reforma tributária congelada). Marcar esses créditos não muda o custo.
            </p>
          </div>

          <div>
            <Label htmlFor="a-motivo" className={ROTULO}>
              Motivo (opcional)
            </Label>
            <Input id="a-motivo" maxLength={300} placeholder="Ex.: nova tabela do fornecedor" aria-invalid={errors.motivo ? true : undefined} {...form.register('motivo')} />
            <MensagemCampo id="a-motivo-erro" mensagem={errors.motivo?.message} />
          </div>

          {erro !== null && <ErroFormacao erro={erro} titulo="Não foi possível registrar o custo de aquisição" />}
          {ok && (
            <p role="status" className="text-small text-success-ink">
              {ok}
            </p>
          )}
          {podeRegistrar && (
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={registrar.isPending || previaImpacto.isPending}
                className="h-9 rounded-[10px] bg-primary px-3.5 text-small font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                {registrar.isPending ? 'Registrando…' : 'Registrar vigência'}
              </button>
              {isDirty && (
                <button type="button" onClick={() => form.reset(padrao)} className="h-9 rounded-[10px] border border-border bg-card px-3.5 text-small hover:bg-line-soft">
                  Descartar
                </button>
              )}
            </div>
          )}
        </fieldset>
      </form>

      <CustoAoVivo comCusto={comCusto} lido={lido.success} calculando={calculando} simulacao={simulacao} corpo={corpo} />
      {dialogo}
    </div>
  );
}

/** Vigencias abertas (validTo nulo), uma por fornecedor, com o selo Preferencial. So leitura; vem do historico ja carregado pela tela. */
function VigenciasAbertas({ custos }: { custos: CustoAquisicaoResponse[] | undefined }) {
  const abertas = (custos ?? []).filter((c) => c.validTo === null);
  if (abertas.length === 0) return null;
  return (
    <section aria-label="Vigências abertas por fornecedor" className="rounded-xl border border-border p-4" data-testid="vigencias-abertas">
      <p className="mb-2 text-small font-semibold">Vigências abertas por fornecedor</p>
      <ul className="flex flex-col gap-1.5 text-small">
        {abertas.map((c) => (
          <li key={c.id} className="flex flex-wrap items-center justify-between gap-2" data-testid="vigencia-aberta">
            <span>
              {c.fornecedorNome ?? 'Fornecedor não informado'} <span className="text-ink-muted">· desde {formatarData(c.validFrom)}</span>
            </span>
            <span className="flex items-center gap-2">
              <span className="tabular font-medium">{formatarCusto(c.acquisitionCost)}</span>
              {c.preferencial && (
                <span className="rounded-full bg-success-bg px-2 py-0.5 text-caption font-semibold text-success-ink" data-testid="selo-preferencial">
                  Preferencial
                </span>
              )}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Switch "recuperavel" + aliquota em % na mesma linha. O switch e independente da taxa (taxa com switch desligado entra no custo). */
function Recuperavel({ form, switchName, taxaName, rotulo, tributo, ajuda }: { form: Formulario; switchName: 'switchIcms' | 'switchIPI' | 'switchPis' | 'switchCofins'; taxaName: 'creditIcms' | 'ipiRate' | 'creditPis' | 'creditCofins'; rotulo: string; tributo: string; ajuda: string }) {
  const id = `a-${switchName}`;
  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={id} className="flex cursor-pointer items-start gap-2">
        <input id={id} type="checkbox" role="switch" aria-describedby={`${id}-ajuda`} className="mt-0.5 size-[18px] accent-primary" {...form.register(switchName)} />
        <span>
          {rotulo}
          <span id={`${id}-ajuda`} className={`block ${AJUDA}`}>
            {ajuda}
          </span>
        </span>
      </label>
      <div>
        <Label htmlFor={`a-${taxaName}`} className={ROTULO}>
          Alíquota do {tributo} (%)
        </Label>
        <Input id={`a-${taxaName}`} inputMode="decimal" className="font-mono" placeholder="0" aria-invalid={form.formState.errors[taxaName] ? true : undefined} aria-describedby={`a-${taxaName}-erro`} {...form.register(taxaName)} />
        <MensagemCampo id={`a-${taxaName}-erro`} mensagem={form.formState.errors[taxaName]?.message} />
      </div>
    </div>
  );
}

/** Custo ao vivo: so exibe o que a API devolveu (acquisitionCost, credito, composicao). Nada e recalculado aqui. */
function CustoAoVivo({
  comCusto,
  lido,
  calculando,
  simulacao,
  corpo,
}: {
  comCusto: boolean;
  lido: boolean;
  calculando: boolean;
  simulacao: ReturnType<typeof useSimularCustoAquisicao>;
  corpo: SimularCustoAquisicaoDto | null;
}) {
  const r = simulacao.data;
  const detalhe = r ? detalheCustoAquisicaoSchema.safeParse(r.detalhe) : null;
  // Tributo com taxa e switch desligado: nao credita, fica dentro do custo (a API ja mostra os creditos que aplicou).
  const entramNoCusto = r && corpo
    ? [
        { t: 'ICMS', ligado: r.switchIcms, taxa: corpo.creditIcms },
        { t: 'PIS', ligado: r.switchPis, taxa: corpo.creditPis },
        { t: 'COFINS', ligado: r.switchCofins, taxa: corpo.creditCofins },
      ].filter((x) => !x.ligado && !ehZero(x.taxa))
    : [];
  return (
    <aside aria-label="Custo de aquisição ao vivo" className="flex flex-col gap-3 rounded-xl border border-border bg-surface-muted p-4">
      <h3 className="text-body font-semibold">Custo de aquisição (ao vivo)</h3>
      {!comCusto ? (
        <p role="status" className="text-small text-ink-muted">
          Sem permissão para ver o custo.
        </p>
      ) : !lido ? (
        <p role="status" className="text-small text-ink-muted">
          Corrija os campos destacados para ver o custo.
        </p>
      ) : simulacao.isError && !r ? (
        <ErroFormacao erro={simulacao.error} titulo="Não foi possível calcular o custo" />
      ) : !r ? (
        <p role="status" className="text-small text-ink-muted">
          Calculando…
        </p>
      ) : (
        <div data-testid="custo-ao-vivo" aria-busy={calculando || simulacao.isFetching}>
          <div className="tabular text-metric font-semibold" data-testid="custo-ao-vivo-valor">
            {formatarCusto(r.acquisitionCost)}
          </div>
          <div className="text-caption text-ink-muted">Custo de aquisição por unidade (sem gravar)</div>
          <table className="mt-3 w-full border-collapse text-small">
            <tbody>
              <tr className="border-t border-line-soft">
                <td className="py-1.5 pr-2">Créditos tributários</td>
                <td className="tabular py-1.5 text-right">{formatarCusto(r.acquisitionTaxCredit)}</td>
              </tr>
              {detalhe?.success &&
                detalhe.data.credits.map((c) => (
                  <tr key={`c-${c.tax}`} className="border-t border-line-soft">
                    <td className="py-1.5 pr-2 text-ink-secondary">(−) Crédito de {c.tax} · {formatarTaxa(c.rate)}</td>
                    <td className="tabular py-1.5 text-right">{formatarCusto(c.value)}</td>
                  </tr>
                ))}
              {detalhe?.success &&
                detalhe.data.additions.map((a) => (
                  <tr key={`a-${a.kind}`} className="border-t border-line-soft">
                    <td className="py-1.5 pr-2 text-ink-secondary">(+) {a.kind} · {formatarTaxa(a.rate)}</td>
                    <td className="tabular py-1.5 text-right">{formatarCusto(a.value)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
          <ul className="mt-3 flex flex-col gap-1 text-caption text-ink-secondary">
            {entramNoCusto.map((x) => (
              <li key={x.t} data-testid="entra-no-custo">
                {x.t} {formatarTaxa(x.taxa)} com switch desligado: entra no custo (não credita).
              </li>
            ))}
            <li>{r.switchIPI ? 'IPI recuperável: fora do custo.' : 'IPI desligado: entra no custo (se houver alíquota).'}</li>
            <li>IBS e CBS: só cadastro, fora do custo.</li>
          </ul>
          <div className={cn('mt-3 text-caption text-ink-muted', calculando && 'opacity-60')}>{calculando ? 'Atualizando…' : `Motor ${r.motorVersao}`}</div>
        </div>
      )}
    </aside>
  );
}
